// The chat SESSION hook — the small client-only view-state the global chat dock drives, and the THIN
// TRANSPORT to the agent (N-CHAT-1..4). It owns NO conversation logic: a send routes to `agent.spawnAgentRun`
// (via the SurfaceApi), the transcript renders EXACTLY `agent.getRunTranscript`, and an interrupt routes to
// `agent.interruptAgentRun` (human-only). The dashboard reasons about nothing, assembles no Context, and is
// NOT screen-aware — the send payload is ONLY the human's typed message (no screen id — N-CHAT-1 AC-4).
//
// HONEST STATE (P-SELF, G-160): the human's OWN typed message is shown immediately as a `human` turn (it is
// the human's input, preserved and never silently dropped even when the send is refused/queued — G-160). The
// AGENT's turns are NEVER fabricated — they come only from the transcript projection the owning module
// resolved (a fresh run with no turn yet renders no agent turn). The run STATE (running / interrupted / done)
// is whatever the transcript read reports; the dock never sets it (N-CHAT-2 AC-1).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { clearResumePointer, useResumeAcrossReload, writeResumePointer } from './chat-resume.ts';
import { useChatSendAction, type ChatSendStores } from './chat-send-action.ts';
import { composeChatTurns } from './chat-turns.ts';
import type { ChatStreamState, ChatTurn } from './chat-turns.ts';
import { clearStreamRetry } from './chat-stream-retry.ts';
// The live-preview state machine lives in its own module (size ceiling) — this file stays transport +
// view-state. EMPTY_CHAT_STREAM is imported rather than re-created so every clear path resets to the SAME
// value the reconcile compares against.
import { EMPTY_CHAT_STREAM, openStream } from './chat-stream-lifecycle.ts';
import { applySessionRead, useChatWake } from './chat-wake.ts';
import type { ChatPlanView, ChatTranscriptView } from './projections.ts';
import type { PendingChatRequest } from './chat-request-identity.ts';

export type { ChatTurn } from './chat-turns.ts';

/** One turn the dock renders — either a human turn it is holding locally (the typed input, G-160) or a turn
 *  from the resolved transcript projection. An AGENT turn additionally carries its run's durable sanitised
 *  activity trace (BUILD-DEC-098) so the view renders the work beside the answer — attached from the
 *  projection, never authored here. */
/** The chat conversation view-state + the transport actions. Consumed by the ChatDock. */
export interface ChatController {
  /** The turns to render, in order: every resolved run of this conversation (multi-run — BUILD-DEC-098),
   *  plus any local human turn not yet resolved, plus the in-progress streaming preview turn. */
  readonly turns: readonly ChatTurn[];
  /** The active run's id once a send has spawned one (drives the transcript read + the interrupt control). */
  readonly activeRunId: string | null;
  /** The OPEN conversation's session id (null before the first send / after New conversation). Drives the
   *  history rail's active-row marker; the durable Session stays owned by the agent module. */
  readonly activeSessionId: string | null;
  /** The active run's lifecycle state from the transcript read, or null before the first send. */
  readonly runState: ChatTranscriptView['state'] | null;
  /** Why an interrupted run stopped — see ChatTranscriptView.interruptedBy. */
  readonly interruptedBy: ChatTranscriptView['interruptedBy'];
  /** Agent-owned quiet interval, present only for an inactivity pause. */
  readonly inactivityTimeoutSeconds: ChatTranscriptView['inactivityTimeoutSeconds'];
  /** True while a send is in flight (the composer disables its send control honestly). */
  readonly sending: boolean;
  /** An honest, sanitised note when a send/interrupt was refused or queued (never an internal error — G-197). */
  readonly note: string | null;
  /** The in-progress agent PREVIEW text for the active run, or '' when not streaming (ephemeral view-state,
   *  discarded on end/unmount — the dashboard owns no durable state; agent B stays the record owner). */
  readonly streamingText: string;
  /** True while a live preview is assembling for the active run (drives the calm streaming affordance). */
  readonly streaming: boolean;
  /** The CURRENT plan / to-do list of the open conversation (BUILD-DEC-099), or null when the agent has
   *  authored none — the inline checklist the dock renders and updates as the agent works. */
  readonly plan: ChatPlanView | null;
  /** Send the human's typed message — routes to the agent; shows the human turn immediately (G-160). */
  readonly send: (message: string) => Promise<void>;
  /** Interrupt the active running run (human-only; the agent cannot — N-CHAT-2). */
  readonly interrupt: () => Promise<void>;
  /** Continue the latest inactivity-paused run in this same durable Session. */
  readonly continueRun: () => Promise<void>;
  /** OPEN a past conversation (BUILD-DEC-098): tears down any live stream, reads the owner-scoped session
   *  transcript (every run's turns + activity), and renders it. Mints nothing. */
  readonly openConversation: (sessionId: string) => Promise<void>;
  /** START a fresh conversation: teardown + clear — the NEXT send mints a new platform-owned Session via
   *  the existing POST /chat/session (the client never self-mints ids). */
  readonly newConversation: () => void;
}

/** The mutable stores the send/interrupt actions drive (grouped so the actions hook stays under the ceiling). */
interface ChatStores extends ChatSendStores {
  readonly transcriptRunId: string | undefined;
  /** The ACTIVE run id — the in-flight stream's run when one is open, else the last-read transcript's run.
   *  The interrupt targets THIS id: a human hitting Stop in the window before the first snapshot read
   *  resolves must still route (a transcript-only id silently no-opped there — chat-rebuild fix). */
  readonly activeRunId: string | null;
  /** The CURRENT transcript value (a ref mirror) — the send path rolls it into `priorRuns` when a new
   *  run starts in the same conversation (multi-run threads, BUILD-DEC-098). */
}

/** Build the stable send/interrupt callbacks over the stores. Kept out of the top hook for the size ceiling. */
function useChatActions(
  api: ReturnType<typeof useSurfaceApi>,
  stores: ChatStores,
): Pick<ChatController, 'send' | 'interrupt' | 'continueRun'> {
  const send = useChatSendAction(api, stores);
  const interrupt = useInterruptAction(api, stores);
  const continueRun = useContinueAction(api, stores);
  return { send, interrupt, continueRun };
}

function useInterruptAction(
  api: ReturnType<typeof useSurfaceApi>,
  stores: ChatStores,
): ChatController['interrupt'] {
  return useCallback(async (): Promise<void> => {
    const runId = stores.activeRunId;
    if (runId === null) return;
    const result = await api.interruptRun(runId);
    if (!result.ok) {
      stores.setNote(result.note ?? 'That action was not permitted.');
      return;
    }
    // Tear the live preview down and re-read so the dock surfaces the `interrupted` state the owning module
    // recorded — it never sets it (N-CHAT-2). The preview is ephemeral; the snapshot is the authority. Capture
    // the generation we bump so a conversation switch DURING the re-read cannot clobber the new conversation
    // with this run's view (review fix — interrupt missed the reconcile()/openConversation gen re-check).
    const gen = (stores.streamGenRef.current += 1); // supersede any in-flight stream reconcile (§ER #6)
    clearStreamRetry(stores);
    stores.streamStopRef.current?.();
    stores.streamStopRef.current = null;
    stores.setStream(EMPTY_CHAT_STREAM);
    const view = await api.getChatTranscript(runId);
    if (stores.streamGenRef.current === gen && stores.mountedRef.current) {
      stores.setTranscript(view);
    }
  }, [api, stores]);
}

function useContinueAction(
  api: ReturnType<typeof useSurfaceApi>,
  stores: ChatStores,
): ChatController['continueRun'] {
  return useCallback(async (): Promise<void> => {
    const predecessor = stores.transcriptRef.current;
    if (
      predecessor === null ||
      predecessor.state !== 'interrupted' ||
      predecessor.interruptedBy !== 'inactivity' ||
      stores.sending
    ) {
      return;
    }
    const generation = (stores.sendGenRef.current += 1);
    stores.setSending(true);
    stores.setNote(null);
    let result: Awaited<ReturnType<typeof api.continueRun>>;
    try {
      result = await api.continueRun(predecessor.runId);
    } catch {
      if (stores.mountedRef.current && stores.sendGenRef.current === generation) {
        stores.setNote('Ritona could not resume this run.');
      }
      return;
    } finally {
      if (stores.mountedRef.current && stores.sendGenRef.current === generation) {
        stores.setSending(false);
      }
    }
    if (!stores.mountedRef.current || stores.sendGenRef.current !== generation) return;
    if (!result.ok) {
      stores.setNote(result.note);
      return;
    }
    stores.streamGenRef.current += 1;
    clearStreamRetry(stores);
    stores.streamStopRef.current?.();
    stores.streamStopRef.current = null;
    stores.setPriorRuns((prior) =>
      prior.some((run) => run.runId === predecessor.runId) ? prior : [...prior, predecessor],
    );
    stores.clearTranscript();
    if (stores.sessionIdRef.current !== null) {
      writeResumePointer({ sessionId: stores.sessionIdRef.current });
    }
    openStream(api, result.runId, stores);
  }, [api, stores]);
}

/**
 * Drive one chat conversation. Mints a client Session id lazily on the first send (the durable Session is
 * owned by `agent`; this is the opaque key the send carries — the dashboard mints no business entity). The
 * hook holds the local human turns and the last-read transcript; it composes them for rendering.
 */
/** The controller's per-render VIEW snapshot (the state the memos + return shape read). */
interface ChatViewState {
  readonly localTurns: readonly ChatTurn[];
  readonly transcriptState: ChatTranscriptView | null;
  readonly priorRuns: readonly ChatTranscriptView[];
  readonly activeSessionId: string | null;
  readonly sending: boolean;
  readonly note: string | null;
  readonly stream: ChatStreamState;
}

/** The transcript setters keep a REF MIRROR so the stable action callbacks can roll a finished run into
 *  the prior list (multi-run threads) without threading the state value through their closures. */
function useTranscriptMirror(
  transcriptRef: React.MutableRefObject<ChatTranscriptView | null>,
  setTranscriptState: React.Dispatch<React.SetStateAction<ChatTranscriptView | null>>,
): Pick<ChatStores, 'setTranscript' | 'clearTranscript'> {
  const setTranscript = useCallback(
    (view: ChatTranscriptView): void => {
      transcriptRef.current = view;
      setTranscriptState(view);
    },
    [transcriptRef, setTranscriptState],
  );
  const clearTranscript = useCallback((): void => {
    transcriptRef.current = null;
    setTranscriptState(null);
  }, [transcriptRef, setTranscriptState]);
  return { setTranscript, clearTranscript };
}

/** The live preview is ephemeral: tear the active stream down on unmount so no subscription leaks and no
 *  durable client state survives (Constraint 4 — the dashboard owns no state; agent B is the record owner). */
function useStreamTeardownOnUnmount(
  mountedRef: React.MutableRefObject<boolean>,
  streamStopRef: React.MutableRefObject<(() => void) | null>,
  streamRetryRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
): void {
  useEffect(
    () => () => {
      mountedRef.current = false; // reconcile/interrupt skip setState after their await (§ER #10)
      if (streamRetryRef.current !== null) clearTimeout(streamRetryRef.current);
      streamRetryRef.current = null;
      streamStopRef.current?.();
      streamStopRef.current = null;
    },
    [mountedRef, streamRetryRef, streamStopRef],
  );
}

/** Create all the controller's state/refs + the stores bundle — split from useChat for the size ceiling. */
function useChatStores(): { readonly stores: ChatStores; readonly view: ChatViewState } {
  const sessionIdRef = useRef<string | null>(null);
  const streamStopRef = useRef<(() => void) | null>(null);
  const streamRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamGenRef = useRef(0);
  const sendGenRef = useRef(0);
  const pendingRequestRef = useRef<PendingChatRequest | null>(null);
  const mountedRef = useRef(true);
  const transcriptRef = useRef<ChatTranscriptView | null>(null);
  const [localTurns, setLocalTurns] = useState<readonly ChatTurn[]>([]);
  const [transcriptState, setTranscriptState] = useState<ChatTranscriptView | null>(null);
  const [priorRuns, setPriorRuns] = useState<readonly ChatTranscriptView[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [stream, setStream] = useState(EMPTY_CHAT_STREAM);

  const { setTranscript, clearTranscript } = useTranscriptMirror(transcriptRef, setTranscriptState);
  useStreamTeardownOnUnmount(mountedRef, streamStopRef, streamRetryRef);

  return {
    stores: {
      sessionIdRef,
      sending,
      setLocalTurns,
      setTranscript,
      clearTranscript,
      setSending,
      setNote,
      setStream,
      streamStopRef,
      streamRetryRef,
      streamGenRef,
      sendGenRef,
      pendingRequestRef,
      mountedRef,
      transcriptRunId: transcriptState?.runId,
      activeRunId: stream.runId ?? transcriptState?.runId ?? null,
      transcriptRef,
      setPriorRuns,
      setActiveSessionId,
    },
    view: { localTurns, transcriptState, priorRuns, activeSessionId, sending, note, stream },
  };
}

export function useChat(): ChatController {
  const api = useSurfaceApi();
  const { stores, view } = useChatStores();
  const { localTurns, transcriptState, priorRuns, activeSessionId, stream } = view;

  // A STABLE subscribe callback for the mount-only resume effect: `stores` is recreated every render (it
  // carries the changing `sending`/`activeRunId`), so a callback closing over it directly would change
  // every render and re-fire the resume effect in a loop. Route through a ref to the latest stores instead.
  const storesRef = useRef(stores);
  storesRef.current = stores;
  const subscribeIfRunning = useCallback(
    (runId: string): void => openStream(api, runId, storesRef.current),
    [api],
  );
  useResumeAcrossReload(api, {
    sessionIdRef: stores.sessionIdRef,
    mountedRef: stores.mountedRef,
    setTranscript: stores.setTranscript,
    setPriorRuns: stores.setPriorRuns,
    setActiveSessionId: stores.setActiveSessionId,
    subscribeIfRunning,
    transcriptRunId: transcriptState?.runId,
  });
  // A run this client did not start (an inbound reply waking the same Session) has a run id nothing here
  // ever learns, so nothing streams. While the conversation is open and IDLE, re-read it — see chat-wake.ts.
  const wake = { sessionId: activeSessionId, streaming: stream.runId !== null, subscribeIfRunning };
  useChatWake(api, stores, wake);

  const { send, interrupt, continueRun } = useChatActions(api, stores);
  const { openConversation, newConversation } = useConversationSwitch(api, stores);

  const turns = useMemo<readonly ChatTurn[]>(
    () => composeChatTurns(priorRuns, transcriptState, localTurns, stream),
    [priorRuns, transcriptState, localTurns, stream],
  );

  // While a preview is assembling the run is `running` (module-driven); the terminal state comes only from
  // the resolved snapshot's recorded state, never a client guess (N-CHAT-2 AC-1, P-SELF).
  return {
    turns,
    activeRunId: stream.runId ?? transcriptState?.runId ?? null,
    activeSessionId,
    runState: stream.runId !== null ? 'running' : (transcriptState?.state ?? null),
    interruptedBy: transcriptState?.interruptedBy,
    inactivityTimeoutSeconds: transcriptState?.inactivityTimeoutSeconds,
    sending: view.sending,
    note: view.note,
    streamingText: stream.text,
    streaming: stream.runId !== null,
    // A persist-first live revision wins while the run works. Reconciliation then replaces it with the same
    // Agent-owned Session plan; before the first live revision, retain the latest durable prior snapshot.
    plan: stream.plan ?? transcriptState?.plan ?? priorRuns[priorRuns.length - 1]?.plan ?? null,
    send,
    interrupt,
    continueRun,
    openConversation,
    newConversation,
  };
}

/** The conversation-switch actions (BUILD-DEC-098): open a past conversation / start a fresh one. The
 *  teardown discipline is `interrupt()`'s (§ER #6): bump the stream generation, stop the stream, clear
 *  the ephemeral view-state — then swap the conversation identity and read the owned record. */
function useConversationSwitch(
  api: ReturnType<typeof useSurfaceApi>,
  stores: ChatStores,
): Pick<ChatController, 'openConversation' | 'newConversation'> {
  const teardown = useCallback((): void => {
    stores.streamGenRef.current += 1; // supersede any in-flight stream reconcile (§ER #6)
    stores.sendGenRef.current += 1; // supersede any in-flight SEND (its post-await re-check now aborts)
    stores.pendingRequestRef.current = null;
    clearStreamRetry(stores);
    stores.streamStopRef.current?.();
    stores.streamStopRef.current = null;
    stores.setStream(EMPTY_CHAT_STREAM);
    stores.setLocalTurns([]);
    stores.setNote(null);
    stores.setPriorRuns([]);
    stores.clearTranscript();
    stores.setSending(false); // a superseded in-flight send never reaches its own setSending(false)
  }, [stores]);

  const openConversation = useCallback(
    async (sessionId: string): Promise<void> => {
      teardown();
      // A history selection supersedes the prior reload hint immediately. Persist the selected Session
      // only after its owner-scoped read succeeds; a failed B read must never make a later dock reopen
      // restore stale Session A and bind B's retained draft into it.
      clearResumePointer();
      stores.sessionIdRef.current = sessionId;
      stores.setActiveSessionId(sessionId);
      try {
        const view = await api.getChatSession(sessionId);
        // Re-check after the await: a newer switch/send may have started, or the dock unmounted (§ER #6/#10).
        if (!stores.mountedRef.current || stores.sessionIdRef.current !== sessionId) return;
        // The fold (prior runs + the latest transcript + the resume pointer + subscribing a still-running
        // latest) is SHARED with the wake poll, so a run noticed while the conversation sits open lands
        // exactly the way a reopened conversation does — one apply path, not two that can drift.
        applySessionRead(stores, sessionId, view, (runId) => openStream(api, runId, stores));
      } catch {
        // The owner-scoped read refused (unknown/not-owned — a uniform 404) or the transport failed:
        // surface an honest note, never a fabricated conversation (P-SELF).
        if (stores.mountedRef.current && stores.sessionIdRef.current === sessionId) {
          stores.setNote('That conversation could not be opened.');
        }
      }
    },
    [api, stores, teardown],
  );

  const newConversation = useCallback((): void => {
    teardown();
    stores.sessionIdRef.current = null;
    stores.setActiveSessionId(null);
    clearResumePointer(); // the NEXT send mints a fresh platform-owned Session (POST /chat/session)
  }, [stores, teardown]);

  return { openConversation, newConversation };
}
